import React, { useState } from "react";
import {
  FileText, Monitor, Briefcase, Users, ShieldCheck, UserCircle, Image as ImageIcon,
  Brain, Code, Keyboard, Link, Camera, MousePointer2,
  Database, Check, Minus, ClipboardList, ChevronDown,
} from "lucide-react";

/**
 * The full picture of what a plan grants and how much of it is left.
 *
 * Two questions, answered separately, because they are different questions:
 *   "how much have I used?"  -> counted features, with a bar
 *   "what do I actually have?" -> on/off features, as chips
 *
 * Everything is shown. The old version paginated nine at a time with most of
 * the list commented out, so an organization could not see what it was paying
 * for. A feature that is not granted reads "Not included" in grey rather than
 * "Disabled" in red -- nothing is broken, it simply is not in the plan, and red
 * makes people think something has gone wrong.
 */

// Counted features, in the order they matter to a recruiter. `gate` names the
// boolean that switches the feature on at all: when it is off, the count is
// meaningless and the feature belongs in the "not included" list instead of
// showing a demoralising 0 / 0.
const COUNT_DEFS = [
  { key: "resume_screening_count", title: "Resume Uploads", description: "Resume screening", icon: FileText, gate: "resume_screening_enabled" },
  { key: "interview_count_v2", title: "AI Interviews", description: "Interview sessions", icon: Monitor },
  { key: "hr_count", title: "Team Members", description: "Recruiter seats", icon: Users, gate: "hr_creation_enabled" },
];

// The on/off features shown to customers, in display order: both the
// "Included" and "Not in your plan" lists follow it. Also used by the Plans
// page to name what each plan includes. report_sharing is deliberately not
// listed.
export const BOOL_DEFS = [
  { key: "interactivity_enabled", title: "AI Interview", icon: MousePointer2 },
  { key: "resume_screening_enabled", title: "Resume Screening", icon: FileText },
  { key: "ats_enabled", title: "ATS Integration", icon: Link },
  { key: "video_included", title: "Proctoring", icon: ShieldCheck },
  { key: "create_jd_v1_enabled", title: "Smart Mode JD Creation", icon: Briefcase },
  { key: "create_jd_v2_enabled", title: "Custom Mode JD Creation", icon: ClipboardList },
  { key: "storing_enabled", title: "Video Recording", icon: Database },
  { key: "mcq_enabled", title: "MCQ Assessments", icon: Brain },
  { key: "coding_enabled", title: "Coding Assessments", icon: Code },
  { key: "hr_creation_enabled", title: "Team Members", icon: Users },
  { key: "secondary_camera_enabled", title: "Secondary Camera Monitoring", icon: Camera },
  { key: "live_avatar_enabled", title: "Live Avatar Interviews", icon: UserCircle },
  { key: "behavioral_enabled", title: "Behavioural Assessments", icon: Users },
  { key: "typing_enabled", title: "Typing Assessments", icon: Keyboard },
  { key: "white_label", title: "White Labelling", icon: ImageIcon },
];

const barColor = (used, limit) => {
  if (limit <= 0) return "#D1D5DB";
  const ratio = used / limit;
  if (ratio >= 1) return "#E24B4A";
  if (ratio >= 0.75) return "#EF9F27";
  return "#8B5CF6";
};

// A full-width strip under the page title: plan, expiry and credit meters in
// one row, with what the plan includes behind "See more". The plans are the
// point of the page, so this stays one row until it is opened.
const SubscriptionUsageBox = ({ data, title = "Plan Details", defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);

  if (!data) return null;

  const { subscription, usage } = data;
  if (!subscription) return null;

  const granted = subscription.granted_features || {};
  const countable = granted.countable_features || {};
  const booleans = granted.boolean_features || {};
  const remaining = usage?.remaining || {};
  // Extra credits bought through "Extend Plan" live in custom_addons as flat
  // keys and are already folded into usage.remaining, but never into the base
  // grant -- so the limit has to add them back or the bar reads over 100%.
  const addons = subscription.custom_addons || {};
  // Unused credits an upgrade brought over from the previous plan; also in
  // usage.remaining but not in the new plan's own grant.
  const carried = subscription.carried_over || {};

  const counted = [];
  const notIncluded = [];

  COUNT_DEFS.forEach((def) => {
    const limit = (Number(countable[def.key]) || 0) + (Number(addons[def.key]) || 0)
      + (Number(carried[def.key]) || 0);
    const gateOpen = def.gate ? (booleans[def.gate] ?? addons[def.gate] ?? false) : true;

    if (!gateOpen || limit <= 0) {
      if (def.hideWhenZero) return;
      notIncluded.push({ ...def, id: def.key });
      return;
    }
    const left = remaining[def.key] ?? limit;
    counted.push({
      ...def,
      id: def.key,
      limit,
      used: Math.max(0, limit - left),
      left: Math.max(0, left),
    });
  });

  const included = [];
  BOOL_DEFS.forEach((def) => {
    const on = booleans[def.key] ?? addons[def.key] ?? false;
    // A countable already listed as not-included would say the same thing twice.
    const alreadyListed = notIncluded.some((n) => n.gate === def.key);
    if (on) included.push({ ...def, id: def.key });
    else if (!alreadyListed && def.key in booleans) notIncluded.push({ ...def, id: def.key });
  });

  // display_name, not plan_name: the trial plan is stored as "Trial2"
  // because a retired document already occupies the name "Trial".
  const planName = subscription.plan_display_name || subscription.plan_name || "Trial";
  const endDate = subscription.end_date
    ? new Date(subscription.end_date).toLocaleDateString("en-GB", {
        day: "2-digit", month: "short", year: "numeric",
      })
    : null;
  const daysLeft = subscription.days_remaining ?? (subscription.end_date
    ? Math.max(0, Math.ceil((new Date(subscription.end_date).getTime() - Date.now()) / 86400000))
    : null);

  return (
    <div className="w-full rounded-xl border border-[#EBEAED] dark:border-gray-700/80 bg-bgSecondary text-gray-800 dark:text-white">
      {/* ── One horizontal strip: plan | credit meters | days left + See more ── */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-5 lg:gap-8 px-6 py-5">
        <div className="min-w-0 lg:w-[220px] shrink-0">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-[#9CA3AF]">
            {title}
          </p>
          <p className="text-[18px] font-semibold text-[#404040] dark:text-[#AEB9E1] tracking-wide mt-0.5 truncate">
            {planName}
          </p>
          {/* Not "renews": auto_renew is false on every subscription and
              there is no renewal mechanism, so this date is when access
              ends, not when it is billed again. */}
          {endDate && (
            new Date(subscription.end_date).getTime() <= Date.now() ? (
              // Credits stop being spendable at this date, so say it plainly.
              <p className="text-[12px] font-medium text-red-500">Ended on {endDate}</p>
            ) : (
              <p className="text-[12px] text-gray-500 dark:text-gray-400">Valid until {endDate}</p>
            )
          )}
        </div>

        {counted.length > 0 && (
          // One column per meter, so two meters fill the row instead of leaving
          // a third of it empty.
          <div className={`flex-1 grid grid-cols-1 gap-x-8 gap-y-5 lg:border-l lg:pl-8 border-[#EBEAED] dark:border-gray-700/60 ${
            counted.length >= 3 ? "sm:grid-cols-3" : counted.length === 2 ? "sm:grid-cols-2" : ""
          }`}>
            {counted.map((ft) => {
              const pct = ft.limit > 0 ? Math.min(100, (ft.used / ft.limit) * 100) : 0;
              return (
                <div key={ft.id} className="min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-[12px] text-[#6B7280] dark:text-gray-400 truncate">{ft.title}</span>
                    <span className="text-[13px] font-semibold text-[#404040] dark:text-white whitespace-nowrap">
                      {ft.used}
                      <span className="text-[#9CA3AF] font-medium"> / {ft.limit}</span>
                    </span>
                  </div>
                  <div className="mt-2 h-1.5 w-full rounded-full bg-[#F1F0F4] dark:bg-gray-800 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-300"
                      style={{ width: `${pct}%`, backgroundColor: barColor(ft.used, ft.limit) }}
                    />
                  </div>
                  <p className="mt-1.5 text-[11px] text-[#9CA3AF] dark:text-gray-500">{ft.left} remaining</p>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex lg:flex-col items-center lg:items-end justify-between gap-3 shrink-0">
          {daysLeft !== null && (
            <span
              className={`px-2.5 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap ${
                daysLeft <= 7
                  ? "bg-[#FFF4F4] text-[#B23B3B] dark:bg-red-950/30 dark:text-red-300"
                  : "bg-[#2F64FF]/10 text-[#2F64FF] dark:text-[#AEB9E1]"
              }`}
            >
              {daysLeft === 0 ? "Ends today" : `${daysLeft} day${daysLeft === 1 ? "" : "s"} left`}
            </span>
          )}
          {(included.length > 0 || notIncluded.length > 0) && (
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#2F64FF] hover:opacity-80 whitespace-nowrap"
            >
              {open ? "See less" : "See more"}
              <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
          )}
        </div>
      </div>

      {/* ── See more: what the plan includes and what it does not ── */}
      {open && (
        <div className="border-t border-[#EBEAED] dark:border-gray-700/60 px-6 py-5">
          {included.length > 0 && (
            <>
              <p className="text-[13px] font-semibold text-[#404040] dark:text-[#AEB9E1] mb-3">
                Included in your plan
              </p>
              <div className="flex flex-wrap gap-2">
                {included.map((ft) => (
                  <span
                    key={ft.id}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold bg-[#EAF7EC] text-[#3E8C48] dark:bg-green-900/30 dark:text-green-300"
                  >
                    <Check className="w-3.5 h-3.5" strokeWidth={3} />
                    {ft.title}
                  </span>
                ))}
              </div>
            </>
          )}

          {/* A separate, labelled group rather than a grey chip mixed in among
              the green ones: one row of two colours makes people decode the
              colour to find out what they have. */}
          {notIncluded.length > 0 && (
            <>
              <p className={`text-[13px] font-semibold text-[#9CA3AF] dark:text-gray-500 mb-3 ${included.length ? "mt-5" : ""}`}>
                Not in your plan
              </p>
              <div className="flex flex-wrap gap-2">
                {notIncluded.map((ft) => (
                  <span
                    key={`off-${ft.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium bg-[#F4F4F6] text-[#9CA3AF] dark:bg-gray-800/60 dark:text-gray-500"
                  >
                    <Minus className="w-3.5 h-3.5" strokeWidth={3} />
                    {ft.title}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SubscriptionUsageBox;
